import { FIELD_TYPES } from '../constants/fieldTypes';
import {
  asyncDebounce,
  getObjectToNumberArray,
  isArrayEmpty,
  trimString,
} from './helpers';
import { forbiddenCharacters, isEmpty, validateLength } from './validation';
import { ReactComponent as EditIcon } from '../assets/icons/edit-dark.svg';
import { ReactComponent as DuplicateIcon } from '../assets/icons/content_copy.svg';
import { ReactComponent as DeleteIcon } from '../assets/icons/delete-outlined.svg';
import { API_UNIQUE_DIVISION } from '../constants/apiRoutes';
import http from './http';

const prepareDepartmentsTableData = (
  departments,
  renderDescription,
  renderPeople,
  renderActions,
  division
) => {
  return departments.map(department => {
    return {
      id: department.id,
      description: renderDescription(department),
      people: renderPeople(department, division),
      actions: renderActions(department, division),
    };
  });
};

export const prepareDivisionsTableData = (
  divisions,
  renderDescription,
  renderPeople,
  renderActions = () => {}
) => {
  return divisions.map(division => ({
    ...division,
    departments: !isArrayEmpty(division.departments)
      ? prepareDepartmentsTableData(
          division.departments,
          renderDescription,
          renderPeople,
          renderActions,
          division
        )
      : [],
  }));
};

export const prepareMenuItems = (
  translations,
  record,
  recordParent,
  canEdit,
  canDelete,
  onDelete,
  onEdit,
  onDuplicate
) => [
  {
    type: 'edit',
    label: translations.edit,
    shouldItemRender: () => canEdit,
    action: () => onEdit(record, recordParent),
    icon: EditIcon,
  },
  {
    type: 'duplicate',
    label: translations.duplicate,
    shouldItemRender: () => canEdit,
    action: () => onDuplicate(record, recordParent),
    icon: DuplicateIcon,
  },
  {
    type: 'delete',
    label: translations.delete,
    shouldItemRender: () => canDelete,
    action: () => onDelete(record),
    icon: DeleteIcon,
  },
];

export const prepareInitialData = (data, isEdit, isDepartment = false) => {
  if (isEdit) {
    return {
      id: data.id,
      name: data.name,
      ...(isDepartment ? { description: data.description } : {}),
      ...(isDepartment ? { users: data.users } : {}),
    };
  }
  return {
    name: '',
    ...(isDepartment ? { description: '' } : {}),
    ...(isDepartment ? { users: [] } : {}),
  };
};

const DIVISION_INFO = {
  NAME: 'name',
  DIVISION: 'division',
  DEPARTMENT: 'department',
};

export const prepareInitialDataToDuplicate = (division, department) => {
  return {
    [DIVISION_INFO.NAME]: department.name,
    [DIVISION_INFO.DIVISION]: division?.id,
  };
};

export const prepareFieldsToDuplicate = (subtitle = '') => {
  return [
    {
      name: 'pathSubtitle',
      type: FIELD_TYPES.SUBTITLE,
      label: subtitle,
      variant: 'body2',
    },
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      required: true,
      shouldValidateInitialValue: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'titleMaxLength',
        },
      ],
    },
    {
      name: DIVISION_INFO.DIVISION,
      type: FIELD_TYPES.SELECT,
      optionsKey: 'divisions',
      shouldRemoveFieldSpacing: true,
      parser: {
        value: 'id',
        label: 'name',
      },
    },
  ];
};

export const DIVISION_FIELDS = [
  {
    name: 'name',
    type: FIELD_TYPES.TEXT,
    required: true,
    shouldValidateInitialValue: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'titleMaxLength',
      },
      {
        type: 'uniqueDivisionName',
        validator: asyncDebounce(divisionName => {
          const params = {
            name: trimString(divisionName),
          };
          return http
            .get(API_UNIQUE_DIVISION, { params })
            .then(({ data }) => !data);
        }, 300),
        async: true,
      },
    ],
  },
];

export const prepareDepartmentFields = (subtitle = '') => {
  return [
    {
      name: 'pathSubtitle',
      type: FIELD_TYPES.SUBTITLE,
      label: subtitle,
      variant: 'body2',
    },
    {
      name: 'name',
      type: FIELD_TYPES.TEXT,
      required: true,
      validators: [
        {
          validator: isEmpty,
          type: 'required',
        },
        {
          validator: forbiddenCharacters,
          type: 'forbiddenCharacters',
        },
        {
          validator: fieldValue => validateLength(fieldValue, 0, 255),
          type: 'titleMaxLength',
        },
      ],
    },
    {
      name: 'description',
      type: FIELD_TYPES.RICH_TEXT_EDITOR,
      required: true,
      shouldRemoveFieldSpacing: true,
      validators: [
        {
          type: 'departmentMaxLength',
          validator: fieldValue => validateLength(fieldValue, 0, 2500),
        },
      ],
    },
  ];
};

export const prepareAssignedUsersToDepartmentFields = (subtitle = '') => {
  return [
    {
      name: 'pathSubtitle',
      type: FIELD_TYPES.SUBTITLE,
      label: subtitle,
      variant: 'body2',
    },
    {
      name: 'assignUsers',
      type: FIELD_TYPES.SELECT,
      optionsKey: 'allUsers',
      isUser: true,
      isMulti: true,
      showSelection: true,
      shouldCalculateSelectionHeight: true,
      parser: {
        value: 'id',
        label: 'name',
      },
    },
  ];
};

export const prepareInitialUsersData = assignedTo => ({
  assignUsers: getObjectToNumberArray(assignedTo),
});
